import React, { useMemo, useState } from 'react';
import { AlertTriangle, Loader2, UserCog, X } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { logAction } from '../../../src/lib/logger';
import { validarCNPJ, validarCPF } from '../../../src/lib/utils';
import { propsFundoModal } from '../../../src/lib/backdropModal';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { dadosTitularMudaram } from '../lib/janelaFaturamentoLista';
import { podeEditarCadastro, MENSAGEM_CADASTRO_TRAVADO } from '../../../src/lib/permissaoCadastro';
import { gravarComMotivo } from '../../../src/services/cadastroUc';

// Aberto no lugar de "Retentar" quando a Equatorial recusou CPF/CNPJ ou data de
// nascimento: o robô só volta a tentar depois que o cadastro da UC mudar.

interface Props {
  /** Cobrança da Janela — precisa de `ucData` (cadastro da UC). */
  item: any;
  /** Linha da fila com os dados usados na tentativa recusada. */
  tentativa: { id: string; cpf_cnpj?: string | null; data_nascimento?: string | null; error_details?: string | null };
  onClose: () => void;
  onSalvo: () => void;
}

const formatarDocumento = (digitos: string) => {
  if (digitos.length === 11) return digitos.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (digitos.length === 14) return digitos.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return digitos || '—';
};

const hojeIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const AtualizarDadosTitularModal: React.FC<Props> = ({ item, tentativa, onClose, onSalvo }) => {
  const { user, profile } = useAuth();
  const { addToast } = useToast();
  const uc = item.ucData;
  // #6239: CPF/CNPJ e nascimento do titular são dados cadastrais — só Financeiro ou adm.
  const podeEditar = podeEditarCadastro(profile);

  const [documento, setDocumento] = useState<string>(String(uc?.cpf_cnpj_titular || '').replace(/\D/g, ''));
  const [nascimento, setNascimento] = useState<string>(String(uc?.data_nascimento_titular || '').slice(0, 10));
  const [salvando, setSalvando] = useState(false);

  const digitos = documento.replace(/\D/g, '');
  const ehCpf = digitos.length === 11;
  const docTentado = String(tentativa.cpf_cnpj || '').replace(/\D/g, '');
  // A fila guarda a data como dd/mm/aaaa; o input de data trabalha em aaaa-mm-dd.
  const nascimentoTentadoIso = String(tentativa.data_nascimento || '').replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, '$3-$2-$1');

  const erro = useMemo(() => {
    if (digitos.length !== 11 && digitos.length !== 14) return 'Informe um CPF (11 dígitos) ou CNPJ (14 dígitos).';
    if (ehCpf && !validarCPF(digitos)) return 'CPF inválido — confira os dígitos.';
    if (!ehCpf && !validarCNPJ(digitos)) return 'CNPJ inválido — confira os dígitos.';
    if (ehCpf && !nascimento) return 'Para CPF, a Equatorial exige a data de nascimento do titular.';
    if (ehCpf && nascimento >= hojeIso()) return 'A data de nascimento não pode ser hoje nem no futuro.';
    const novo = { cpf_cnpj_titular: digitos, data_nascimento_titular: ehCpf ? nascimento : null };
    if (!dadosTitularMudaram(tentativa, novo)) {
      return 'São os mesmos dados que a Equatorial recusou. Confirme com o cliente e corrija o que estiver errado.';
    }
    return null;
  }, [digitos, ehCpf, nascimento, tentativa]);

  const salvar = async () => {
    if (erro || !uc?.id || !podeEditar) return;
    setSalvando(true);
    try {
      const campos: string[] = [];
      if (digitos.replace(/^0+/, '') !== docTentado.replace(/^0+/, '')) campos.push('CPF/CNPJ');
      const payload: Record<string, string> = { cpf_cnpj_titular: digitos };
      if (ehCpf) {
        payload.data_nascimento_titular = nascimento;
        if (nascimento !== nascimentoTentadoIso) campos.push('data de nascimento');
      }

      const { data, error } = await gravarComMotivo(
        p => supabase.from('unidades_consumidoras').update(p).eq('id', uc.id).select('id'),
        payload,
        'A Equatorial recusou os dados do titular ao buscar a fatura; corrigidos na Janela de Cobrança.',
      );
      if (error) throw error;
      // RLS que barra o UPDATE não dá erro — só devolve zero linhas.
      if (!data?.length) throw new Error('Sem permissão para alterar o cadastro desta UC.');

      const autor = profile?.full_name || user?.email || 'usuário';
      // Aguardado (é rápido) para o relatório já trazer a correção quando a tela recarregar a fila.
      const { error: erroHistorico } = await supabase.rpc('fdq_registrar_execucao', {
        p_queue_id: tentativa.id,
        p_entrada: {
          timestamp: new Date().toISOString(),
          tentativa: 0,
          status: 'dados_atualizados',
          mensagem: `Cadastro corrigido por ${autor}: ${campos.join(' e ')}.`,
        },
      });
      if (erroHistorico) console.error('[Equatorial] falha ao registrar correção no histórico:', erroHistorico);

      logAction(user?.id, 'ATUALIZAR_DADOS_TITULAR', {
        uc_id: uc.id,
        fila_id: tentativa.id,
        campos,
        contexto: 'Janela de Cobrança — dados recusados pela Equatorial',
      });

      addToast('Dados do titular atualizados. O robô já pode tentar de novo.', 'success');
      onSalvo();
    } catch (e: any) {
      console.error('[AtualizarDadosTitular]', e);
      addToast(e?.message || 'Erro ao atualizar os dados do titular.', 'error');
    } finally {
      setSalvando(false);
    }
  };

  const campoClasse = 'w-full bg-gray-100 dark:bg-gray-700 border-none rounded-xl px-4 py-2.5 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
      style={{ zIndex: 8100 }}
      {...propsFundoModal(onClose)}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md border dark:border-gray-700 overflow-hidden animate-in zoom-in duration-200" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 text-orange-600 rounded-full flex items-center justify-center shrink-0">
                <UserCog size={20} />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Atualizar dados do titular</h3>
                <p className="text-xs text-gray-400 truncate" title={item.nome}>
                  UC {item.uc}{item.outro_numero_uc ? ` · ${item.outro_numero_uc}` : ''} · {item.nome || '—'}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" aria-label="Fechar">
              <X size={20} />
            </button>
          </div>

          <div className="rounded-xl p-3 mb-5 bg-orange-50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-900/30 text-xs text-orange-800 dark:text-orange-300">
            <p className="font-bold mb-1 flex items-center gap-1"><AlertTriangle size={12} /> A Equatorial recusou estes dados</p>
            <p className="mb-2">{tentativa.error_details || 'CPF/CNPJ ou data de nascimento não reconhecidos.'}</p>
            <p>
              Usado na busca: <span className="font-semibold">{formatarDocumento(docTentado)}</span>
              {docTentado.length === 11 && (
                <> · nascimento <span className="font-semibold">{tentativa.data_nascimento || 'não informado'}</span></>
              )}
            </p>
          </div>

          {!uc?.id ? (
            <p className="text-sm text-gray-500">
              Esta cobrança não está ligada a uma UC do cadastro. Corrija os dados pela gestão de UCs.
            </p>
          ) : !podeEditar ? (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {MENSAGEM_CADASTRO_TRAVADO} Passe a eles o CPF/CNPJ e a data de nascimento corretos do titular; depois de corrigido, o robô tenta de novo.
            </p>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-1">CPF/CNPJ do titular da conta</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={documento}
                  onChange={(e) => setDocumento(e.target.value)}
                  placeholder="Somente números"
                  className={campoClasse}
                  autoFocus
                />
                {digitos.length > 0 && <p className="text-[11px] text-gray-400 mt-1">{formatarDocumento(digitos)}</p>}
              </div>
              {ehCpf && (
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Data de nascimento do titular</label>
                  <input type="date" value={nascimento} max={hojeIso()} onChange={(e) => setNascimento(e.target.value)} className={campoClasse} />
                </div>
              )}
              {erro && <p className="text-xs font-medium text-red-600 dark:text-red-400">{erro}</p>}
            </div>
          )}
        </div>

        <div className="p-6 bg-gray-50 dark:bg-gray-800/50 flex gap-3 border-t dark:border-gray-700">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-white rounded-xl font-bold hover:bg-gray-300 transition-all text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={salvar}
            disabled={!!erro || salvando || !uc?.id || !podeEditar}
            className="flex-1 py-3 bg-orange-600 text-white rounded-xl font-bold hover:bg-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
          >
            {salvando ? <Loader2 size={18} className="animate-spin" /> : 'Salvar dados'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AtualizarDadosTitularModal;
